// ============================================
// 🏠 홈 화면 컴포넌트 - 미니멀 + 이모티콘 버전 + 프로필 사진
// ============================================
import React, { useState, useEffect } from 'react';
import { db, auth, analytics, logEvent } from '../firebase';
import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';

function Home({ onNavigateToRecord }) {
  const [userInfo, setUserInfo] = useState(null);
  const [stats, setStats] = useState({
    totalRecords: 0,
    totalSteps: 0,
    uniqueStairs: 0,
    todaySteps: 0,
    recentRecords: [],
  });
  const [myGroups, setMyGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        setUserInfo(data);

        // 대표 그룹 로드
        if (data.primaryGroupId) {
          const primaryGroupDoc = await getDoc(
            doc(db, 'groups', data.primaryGroupId)
          );
          if (primaryGroupDoc.exists()) {
            setMyGroups([
              {
                id: primaryGroupDoc.id,
                ...primaryGroupDoc.data(),
              },
            ]);
          }
        }
      }

      // 기록 로드
      const recordsSnapshot = await getDocs(
        query(
          collection(db, 'user_records'),
          where('userId', '==', auth.currentUser.uid)
        )
      );

      const records = recordsSnapshot.docs.map((doc) => doc.data());

      const totalRecords = records.length;
      const totalSteps = records.reduce(
        (sum, record) => sum + (record.steps || 0),
        0
      );
      const uniqueStairs = new Set(records.map((r) => r.stairId)).size;

      // 오늘 기록
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayRecords = records.filter((r) => {
        const recordDate = r.timestamp.toDate();
        recordDate.setHours(0, 0, 0, 0);
        return recordDate.getTime() === today.getTime();
      });
      const todaySteps = todayRecords.reduce(
        (sum, record) => sum + (record.steps || 0),
        0
      );

      // 최근 기록 5개
      const sortedRecords = records.sort(
        (a, b) => b.timestamp.toMillis() - a.timestamp.toMillis()
      );
      const recentRecords = sortedRecords.slice(0, 5);

      setStats({
        totalRecords,
        totalSteps,
        uniqueStairs,
        todaySteps,
        recentRecords,
      });
    } catch (error) {
      console.error('데이터 로드 실패:', error);
    }
    setLoading(false);
  };

  const calculateHeight = (steps) => {
    const heightInCm = steps * 16.5;
    const heightInM = heightInCm / 100;

    if (heightInM >= 1000) {
      return `${(heightInM / 1000).toFixed(1)}km`;
    } else if (heightInM >= 1) {
      return `${heightInM.toFixed(1)}m`;
    } else {
      return `${heightInCm.toFixed(0)}cm`;
    }
  };

  // ✨ 16단계 목표 시스템
  const getHeightComparison = (steps) => {
    const heightInM = (steps * 16.5) / 100;

    const comparisons = [
      { threshold: 3.3, steps: 20, name: '사람 2명' },
      { threshold: 5.5, steps: 34, name: '기린 1마리' },
      { threshold: 10, steps: 61, name: '아파트 3층' },
      { threshold: 16.5, steps: 100, name: '아파트 5층' },
      { threshold: 33, steps: 200, name: '아파트 10층' },
      { threshold: 50, steps: 303, name: '아파트 15층' },
      { threshold: 100, steps: 606, name: '아파트 30층' },
      { threshold: 264, steps: 1600, name: '63빌딩' },
      { threshold: 324, steps: 1964, name: '에펠탑' },
      { threshold: 381, steps: 2309, name: '엠파이어 스테이트' },
      { threshold: 443, steps: 2685, name: '도쿄타워' },
      { threshold: 555, steps: 3364, name: '롯데월드타워' },
      { threshold: 828, steps: 5018, name: '부르즈 할리파' },
      { threshold: 1950, steps: 11818, name: '서울N타워' },
      { threshold: 2744, steps: 16630, name: '한라산' },
      { threshold: 8849, steps: 53636, name: '에베레스트 산' },
    ];

    let achieved = null;
    let nextGoal = null;

    for (let i = comparisons.length - 1; i >= 0; i--) {
      if (heightInM >= comparisons[i].threshold) {
        achieved = comparisons[i];
        if (i < comparisons.length - 1) {
          nextGoal = comparisons[i + 1];
        }
        break;
      }
    }

    if (!achieved) {
      nextGoal = comparisons[0];
      const remainingSteps = nextGoal.steps - steps;
      return {
        message: `첫 목표(${nextGoal.name})까지 ${calculateHeight(
          remainingSteps
        )} 남았어요 (๑•̀ㅂ•́)و✧`,
        nextGoal,
        achieved: null,
        progress: (steps / nextGoal.steps) * 100,
      };
    }

    if (!nextGoal) {
      return {
        message: `★ 모든 목표 달성!! ٩(ˊᗜˋ*)و`,
        nextGoal: null,
        achieved,
        progress: 100,
      };
    }

    const progress =
      ((steps - achieved.steps) / (nextGoal.steps - achieved.steps)) * 100;

    return {
      message: `★ ${achieved.name} 달성!! ٩(ˊᗜˋ*)و`,
      subtitle: `다음 목표: ${nextGoal.name} (๑˃̵ᴗ˂̵)و`,
      nextGoal,
      achieved,
      progress: Math.min(Math.max(progress, 0), 100),
    };
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const hours = date.getHours();
    const minutes = date.getMinutes();
    return `${month}/${day} ${hours}:${minutes.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div>로딩 중...</div>
      </div>
    );
  }

  const comparison = getHeightComparison(stats.totalSteps);

  return (
    <div style={styles.container}>
      {/* 헤더: 프로필 네모 + 인사 + 날짜 + 대표 그룹 */}
      <div style={styles.header}>
        <div style={styles.headerRow}>
          {/* 왼쪽 프로필 네모 */}
          <div style={styles.profileBox}>
            {userInfo?.photoURL ? (
              <img
                src={userInfo.photoURL}
                alt="프로필"
                style={styles.profileImg}
              />
            ) : (
              <div style={styles.profilePlaceholder}>
                ( •ᴗ• )
              </div>
            )}
          </div>

          {/* 오른쪽 텍스트 영역 */}
          <div>
            <div style={styles.greeting}>
              안녕하세요, {userInfo?.nickname || '딸기밤'}님 ( •ᴗ•♡ )
            </div>
            <div style={styles.date}>
              {new Date().toLocaleDateString('ko-KR', {
                month: 'long',
                day: 'numeric',
              })}
            </div>
            <div style={styles.subGreeting}>
              오늘도 천천히, 하지만 꾸준하게*✧
            </div>
            {myGroups.length > 0 && (
              <div style={styles.headerGroup}>
                대표 그룹: {myGroups[0].name} · 멤버{' '}
                {myGroups[0].members?.length || 0}명
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 가장 잘 보이는 CTA 버튼 */}
      <button
        style={styles.recordButton}
        onClick={() => {
          logEvent(analytics, 'navigate_to_record', {
            from: 'home_cta_button'
          });
          onNavigateToRecord();
        }}
      >
        [ [ [ 올라간 계단 기록하기 ] ] ]
      </button>

      {/* 📌 핵심 정보 통합 섹션 */}
      <div style={styles.infoSection}>
        {/* 오늘의 목표 + 나의 통계 2컬럼 배치 */}
        <div style={styles.twoCol}>
          {/* 왼쪽: 오늘의 목표 */}
          <div style={styles.colItem}>
            <div style={styles.sectionTitle}>★ 오늘의 목표 ★</div>
            <div>출퇴근 계단 오르기</div>
            <div style={{ marginTop: 6 }}>
              오늘: {stats.todaySteps} · 누적: {stats.totalSteps}
            </div>
          </div>

          {/* 오른쪽: 나의 통계 */}
          <div style={styles.colItem}>
            <div style={styles.sectionTitle}>▣ 나의 통계 ▣</div>
            <div>총 기록: {stats.totalRecords}회</div>
            <div>총 계단: {stats.totalSteps}개</div>
            <div>등반 계단: {stats.uniqueStairs}곳</div>
          </div>
        </div>

        {/* 아래: 누적 높이 전체 폭 */}
        <div style={{ marginTop: 20, fontWeight: '600' }}>
          ▲ 누적 높이 ▲
        </div>
        <div style={styles.bigNumber}>{calculateHeight(stats.totalSteps)}</div>
        <div>{comparison.message}</div>
        {comparison.subtitle && (
          <div style={styles.subText}>{comparison.subtitle}</div>
        )}
        {comparison.nextGoal && (
          <div style={styles.remainingSteps}>
            남은 계단: {(comparison.nextGoal.steps - stats.totalSteps).toLocaleString()}개
          </div>
        )}

        {comparison.nextGoal && (
          <div style={styles.progressRow}>
            <div style={styles.progressBar}>
              <div
                style={{
                  ...styles.progressFill,
                  width: `${comparison.progress}%`,
                }}
              />
            </div>
            <div style={styles.progressText}>
              {comparison.progress.toFixed(0)}%
            </div>
          </div>
        )}
      </div>

      {/* 최근 활동 */}
      {stats.recentRecords.length > 0 && (
        <div style={styles.activitySection}>
          <div style={styles.activityTitle}>최근 활동 </div>
          <div style={styles.activityList}>
            {stats.recentRecords.slice(0, 3).map((record, index) => (
              <div key={index} style={styles.activityItem}>
                <div style={styles.activityIcon}>■</div>
                <div style={styles.activityContent}>
                  <div style={styles.activityName}>
                    {record.stairName || '계단'}
                  </div>
                  <div style={styles.activityDetail}>
                    {record.steps}개 · {calculateHeight(record.steps)}
                  </div>
                </div>
                <div style={styles.activityDate}>
                  {formatDate(record.timestamp)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// 🎨 스타일
// ============================================
const styles = {
  container: {
    maxWidth: '600px',
    margin: '0 auto',
    padding: '16px',
    paddingBottom: '40px',
    fontSize: '14px',
    lineHeight: 1.4,
  },
  loadingContainer: {
    minHeight: '80vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
  },

  header: {
    marginBottom: '12px',
    borderBottom: '1px solid #ddd',
    paddingBottom: '8px',
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  profileBox: {
    width: '80px',
    height: '80px',
    overflow: 'hidden',
    backgroundColor: '#eee',
    flexShrink: 0,
  },
  profileImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    border: '1px solid #000',
    display: 'block',
  },
  profilePlaceholder: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '22px',
    color: '#555',
  },

  greeting: {
    fontSize: '16px',
    fontWeight: '600',
  },
  date: {
    fontSize: '12px',
    color: '#555',
    marginTop: '2px',
  },
  subGreeting: {
    fontSize: '12px',
    color: '#555',
    marginTop: '4px',
  },
  headerGroup: {
    fontSize: '13px',
    color: '#333',
    marginTop: '4px',
  },

  recordButton: {
    width: '100%',
    padding: '10px 0',
    margin: '10px 0 10px',
    border: '1px solid #000',
    background: '#808080',
    color: '#fff',
    fontSize: '16px',
    cursor: 'pointer',
  },

  infoSection: {
    padding: '12px 0',
    borderTop: '1px solid #ddd',
    borderBottom: '1px solid #ddd',
    marginTop: '4px',
    marginBottom: '16px',
  },
  sectionTitle: {
    fontWeight: '600',
    marginBottom: 6,
  },
  twoCol: {
    display: 'flex',
    gap: '20px',
    justifyContent: 'space-between',
  },
  colItem: {
    flex: 1,
    minWidth: 0,
  },
  bigNumber: {
    fontSize: '24px',
    fontWeight: '600',
    margin: '4px 0',
  },
  subText: {
    fontSize: '12px',
    color: '#666',
  },
  remainingSteps: {
    fontSize: '13px',
    color: '#555',
    marginTop: '4px',
    fontWeight: '500',
  },
  progressRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: '8px',
  },
  progressBar: {
    flex: 1,
    height: '6px',
    background: '#eee',
    position: 'relative',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    background: '#000',
  },
  progressText: {
    fontSize: '12px',
  },

  activitySection: {
    paddingTop: '8px',
  },
  activityTitle: {
    fontSize: '14px',
    fontWeight: '600',
    marginBottom: '8px',
  },
  activityList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  activityItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 0',
    borderBottom: '1px solid #eee',
  },
  activityIcon: {
    fontSize: '14px',
    width: '32px',
    textAlign: 'center',
    flexShrink: 0,
  },
  activityContent: {
    flex: 1,
    minWidth: 0,
  },
  activityName: {
    fontSize: '13px',
    fontWeight: '500',
    marginBottom: '2px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  activityDetail: {
    fontSize: '12px',
    color: '#555',
  },
  activityDate: {
    fontSize: '12px',
    color: '#555',
    whiteSpace: 'nowrap',
  },
};

export default Home;
